/**
 * Device-gated Amplitude Session Replay.
 *
 * The plugin is never statically imported: callers must dynamic-import it only
 * after {@link evaluateSessionReplayCapability} allows recording. Missing
 * browser APIs are treated as "unknown", not "weak", so Safari (no
 * deviceMemory) is not excluded by default.
 */

export const SESSION_REPLAY_SAMPLE_RATE = 0.05;

export const SESSION_REPLAY_DEVICE_REQUIREMENTS = {
  minDeviceMemoryGb: 4,
  minHardwareConcurrency: 4,
} as const;

export const SESSION_REPLAY_SLOW_EFFECTIVE_TYPES = ['slow-2g', '2g'] as const;

export type SessionReplaySlowEffectiveType =
  typeof SESSION_REPLAY_SLOW_EFFECTIVE_TYPES[number];

export type SessionReplaySkipReason =
  | 'saveData'
  | 'slowNetwork'
  | 'lowMemory'
  | 'lowCpu';

export type SessionReplayCapability =
  | { readonly allowed: true }
  | { readonly allowed: false, readonly reason: SessionReplaySkipReason };

export interface SessionReplayNetworkInformation {
  readonly saveData?: boolean;
  readonly effectiveType?: string;
}

/** Structural navigator surface we actually read — avoids augmenting lib.dom. */
export interface SessionReplayNavigatorLike {
  readonly deviceMemory?: number;
  readonly hardwareConcurrency?: number;
  readonly connection?: SessionReplayNetworkInformation;
}

export interface SessionReplayDeviceSignals {
  readonly deviceMemoryGb?: number;
  readonly hardwareConcurrency?: number;
  readonly saveData?: boolean;
  readonly effectiveType?: string;
}

export function isSlowEffectiveType(
  value: string | undefined,
): value is SessionReplaySlowEffectiveType {
  return value !== undefined
    && (SESSION_REPLAY_SLOW_EFFECTIVE_TYPES as readonly string[]).includes(value);
}

function isPositiveFiniteNumber(value: number | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

function isBelowMinimum(value: number | undefined, minimum: number): boolean {
  return isPositiveFiniteNumber(value) && value < minimum;
}

export function readSessionReplayDeviceSignals(
  nav: SessionReplayNavigatorLike = navigator as SessionReplayNavigatorLike,
): SessionReplayDeviceSignals {
  return {
    deviceMemoryGb: nav.deviceMemory,
    hardwareConcurrency: nav.hardwareConcurrency,
    saveData: nav.connection?.saveData,
    effectiveType: nav.connection?.effectiveType,
  };
}

export function evaluateSessionReplayCapability(
  signals: SessionReplayDeviceSignals,
  requirements: typeof SESSION_REPLAY_DEVICE_REQUIREMENTS = SESSION_REPLAY_DEVICE_REQUIREMENTS,
): SessionReplayCapability {
  if (signals.saveData === true) {
    return { allowed: false, reason: 'saveData' };
  }

  if (isSlowEffectiveType(signals.effectiveType)) {
    return { allowed: false, reason: 'slowNetwork' };
  }

  if (isBelowMinimum(signals.deviceMemoryGb, requirements.minDeviceMemoryGb)) {
    return { allowed: false, reason: 'lowMemory' };
  }

  if (isBelowMinimum(signals.hardwareConcurrency, requirements.minHardwareConcurrency)) {
    return { allowed: false, reason: 'lowCpu' };
  }

  return { allowed: true };
}
